/**
 * Business-ID aus KV, Dexie meta und entity_* ermitteln (ohne Login-Fallback).
 */

import { kvGet, kvSet, listKvKeys } from './kvAccess';
import { getElkheirDb } from './elkheirDb';
import { BUSINESS_PAYLOAD_META_PREFIX } from './businessPayloadMeta';
import { ENTITY_STORE_TABLE_NAMES } from './entityAggregateStore';
import {
  getCurrentBusinessId,
  inferBusinessIdFromExistingPayloads,
  isEntityShellBlob,
  normalizeBusinessStorageId,
  readBestBusinessMergedPayload,
  isEphemeralOfflineBusinessId
} from '../../utils/sharedCore';

/** Root/meta tables written by empty login bootstrap — not operational data. */
const META_ENTITY_TABLES = new Set([
  'entity_business_info',
  'entity_user_info',
  'entity_metadata',
  'entity_reports',
  'entity_stock_data',
  'entity_discrepancy_management',
  'entity_stock_locations'
]);

/** Full IDB scan is expensive — coalesce boot/heal probes within one short window. */
const LIST_KNOWN_TTL_MS = 8000;
/** @type {{ at: number, rows: object[] }|null} */
let listKnownCache = null;
/** @type {Promise<object[]>|null} */
let listKnownInFlight = null;

/**
 * Drop cached discovery rows (after pin/relink/purge or explicit repair).
 */
export function invalidateListKnownBusinessIdsCache() {
  listKnownCache = null;
  listKnownInFlight = null;
}

/**
 * True when a listKnownBusinessIds row has products/lots/purchases/etc.
 * Empty login bootstrap writes noKvBlob + a few meta entity rows — that is NOT real data.
 * Under NO_KV_BLOB, blob score alone must NOT count (avoids relink to a legacy-only bucket).
 */
export function businessBucketHasOperationalData(row) {
  if (!row) return false;

  let noKvBlob = Boolean(row.noKvBlob || row.entityStoreNoKvBlob);
  if (!noKvBlob) {
    try {
      // Lazy require avoids circular init with businessPayloadMeta ↔ sharedCore
      const { isEntityStoreNoKvBlobEnabled } = require('./businessPayloadMeta');
      noKvBlob = isEntityStoreNoKvBlobEnabled();
    } catch {
      /* optional */
    }
  }

  if (noKvBlob) {
    if (row.entityDataRows != null) return (Number(row.entityDataRows) || 0) > 0;
    return (Number(row.entityRows) || 0) > 8;
  }

  if ((Number(row.score) || 0) > 5) return true;
  if (row.entityDataRows != null) return (Number(row.entityDataRows) || 0) > 0;
  // Legacy callers (tests) only pass entityRows
  return (Number(row.entityRows) || 0) > 8;
}

function addId(map, rawId, source, extra = {}) {
  const businessId = normalizeBusinessStorageId(rawId);
  if (!businessId) return;
  const prev = map.get(businessId) || { sources: [] };
  map.set(businessId, {
    ...prev,
    ...extra,
    sources: [...new Set([...prev.sources, source])]
  });
}

function businessIdFromKvKey(key) {
  const s = String(key || '');
  let m = s.match(/^business_(.+)_data$/);
  if (m?.[1]) return normalizeBusinessStorageId(m[1]);
  m = s.match(/^worker_sessions_(.+)$/);
  if (m?.[1]) return normalizeBusinessStorageId(m[1]);
  m = s.match(/^sequences_(.+)$/);
  if (m?.[1]) return normalizeBusinessStorageId(m[1]);
  return null;
}

/**
 * Rohe IndexedDB-Zähler für Diagnose.
 */
export async function getStorageDiagnostics() {
  const diag = {
    kvCacheKeys: listKvKeys().length,
    kvBusinessBlobKeys: listKvKeys().filter((k) => /^business_.+_data$/.test(k)).length,
    currentBusiness: null,
    idbOpen: false,
    idbKvItems: 0,
    idbMeta: 0,
    entityTables: {},
    payloadMetaKeys: []
  };

  const cb = kvGet('currentBusiness');
  if (cb && typeof cb === 'object' && cb.id) {
    diag.currentBusiness = normalizeBusinessStorageId(cb.id);
  }

  try {
    const db = getElkheirDb();
    if (!db.isOpen()) await db.open();
    diag.idbOpen = true;
    diag.idbKvItems = await db.kv_items.count();
    diag.idbMeta = await db.meta.count();

    for (const tableName of ENTITY_STORE_TABLE_NAMES) {
      const table = db[tableName];
      if (!table) continue;
      try {
        diag.entityTables[tableName] = await table.count();
      } catch {
        diag.entityTables[tableName] = -1;
      }
    }

    const metaRows = await db.meta.toArray();
    for (const row of metaRows) {
      const key = String(row?.key || '');
      if (key.startsWith(BUSINESS_PAYLOAD_META_PREFIX)) {
        diag.payloadMetaKeys.push({
          businessId: key.slice(BUSINESS_PAYLOAD_META_PREFIX.length),
          noKvBlob: Boolean(row.value?.noKvBlob),
          payloadVersion: Number(row.value?.payloadVersion) || 0
        });
      }
    }
  } catch (err) {
    diag.error = err?.message || String(err);
  }

  return diag;
}

/**
 * Alle bekannten Business-IDs (KV-Blob, meta, entity_*).
 * Cached briefly — boot/heal often call probe + relink + sticky in the same second.
 * @param {{ force?: boolean }} [options]
 */
export async function listKnownBusinessIds(options = {}) {
  if (options.force === true) {
    invalidateListKnownBusinessIdsCache();
  } else if (
    listKnownCache &&
    Date.now() - listKnownCache.at < LIST_KNOWN_TTL_MS
  ) {
    return listKnownCache.rows.slice();
  }

  if (listKnownInFlight) {
    const shared = await listKnownInFlight;
    return shared.slice();
  }

  listKnownInFlight = scanKnownBusinessIds();
  try {
    const rows = await listKnownInFlight;
    listKnownCache = { at: Date.now(), rows };
    return rows.slice();
  } finally {
    listKnownInFlight = null;
  }
}

async function scanKnownBusinessIds() {
  const map = new Map();

  const cb = kvGet('currentBusiness');
  if (cb && typeof cb === 'object' && cb.id) {
    addId(map, cb.id, 'currentBusiness');
  }

  for (const key of listKvKeys() || []) {
    const id = businessIdFromKvKey(key);
    if (id) addId(map, id, 'kv_cache');
    const m = String(key || '').match(/^business_(.+)_data$/);
    if (m?.[1]) addId(map, m[1], 'kv_blob');
  }

  const fromKvInfer = inferBusinessIdFromExistingPayloads();
  if (fromKvInfer) addId(map, fromKvInfer, 'kv_infer');

  try {
    const db = getElkheirDb();
    if (!db.isOpen()) await db.open();

    const kvRows = await db.kv_items.toArray();
    for (const row of kvRows) {
      const id = businessIdFromKvKey(row?.key);
      if (id) addId(map, id, 'idb_kv_items');
    }

    const metaRows = await db.meta.toArray();
    for (const row of metaRows) {
      const key = String(row?.key || '');
      if (key.startsWith(BUSINESS_PAYLOAD_META_PREFIX)) {
        const id = key.slice(BUSINESS_PAYLOAD_META_PREFIX.length);
        addId(map, id, 'payload_meta', {
          noKvBlob: Boolean(row.value?.noKvBlob),
          payloadVersion: Number(row.value?.payloadVersion) || 0
        });
      }
    }

    const entityCounts = new Map();
    const entityDataCounts = new Map();
    for (const tableName of ENTITY_STORE_TABLE_NAMES) {
      const table = db[tableName];
      if (!table) continue;
      const rows = await table.toArray();
      const isMeta = META_ENTITY_TABLES.has(tableName);
      for (const row of rows) {
        if (!row?.businessId) continue;
        const bid = normalizeBusinessStorageId(row.businessId);
        entityCounts.set(bid, (entityCounts.get(bid) || 0) + 1);
        if (!isMeta) {
          entityDataCounts.set(bid, (entityDataCounts.get(bid) || 0) + 1);
        }
        addId(map, bid, tableName);
      }
    }
    for (const [bid, count] of entityCounts) {
      const prev = map.get(bid) || { sources: [] };
      map.set(bid, {
        ...prev,
        entityRows: count,
        entityDataRows: entityDataCounts.get(bid) || 0
      });
    }
  } catch (err) {
    console.warn('[businessIdDiscovery] IDB scan failed:', err?.message || err);
  }

  const result = [];
  for (const [businessId, meta] of map) {
    const { parsed, score } = readBestBusinessMergedPayload(businessId);
    result.push({
      businessId,
      score: score || 0,
      entityShell: parsed ? isEntityShellBlob(parsed) : false,
      hasBlob: Boolean(parsed),
      ...meta
    });
  }

  result.sort((a, b) => {
    const scoreDiff = (b.score || 0) - (a.score || 0);
    if (scoreDiff !== 0) return scoreDiff;
    return (b.entityRows || 0) - (a.entityRows || 0);
  });

  return result;
}

/**
 * Cheap equality for storage / session ids (strip optional business_ prefix).
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function sameBusinessStorageId(a, b) {
  const na = normalizeBusinessStorageId(a);
  const nb = normalizeBusinessStorageId(b);
  if (na == null || na === '' || nb == null || nb === '') return false;
  return String(na) === String(nb);
}

/**
 * Beste bekannte Business-ID (kein Canvas-Fallback).
 * @param {string} [explicitId]
 */
export async function resolveBestKnownBusinessId(explicitId) {
  if (explicitId) return normalizeBusinessStorageId(explicitId);

  const strict = getCurrentBusinessId({ allowFallback: false });
  if (strict) return strict;

  const fromKv = inferBusinessIdFromExistingPayloads();
  if (fromKv) return fromKv;

  const known = await listKnownBusinessIds();
  const offlineRaw = kvGet('offline_businesses');
  const registeredIds = new Set(
    (Array.isArray(offlineRaw) ? offlineRaw : [])
      .map((b) => normalizeBusinessStorageId(b?.id))
      .filter(Boolean)
  );
  const filtered = known.filter(
    (entry) => !isEphemeralOfflineBusinessId(entry.businessId, registeredIds)
  );
  const withData = filtered.filter(
    (entry) =>
      businessBucketHasOperationalData(entry) ||
      (entry.score || 0) > 0 ||
      entry.hasBlob ||
      registeredIds.has(entry.businessId)
  );
  if (withData.length > 0) return withData[0].businessId;
  if (filtered.length > 0) return filtered[0].businessId;

  return null;
}

/** currentBusiness in KV setzen — stoppt Fallback-Warnungen. */
export function pinCurrentBusiness(businessId) {
  const id = normalizeBusinessStorageId(businessId);
  if (!id) return { ok: false, error: 'invalid_business_id' };
  const existing = kvGet('currentBusiness');
  const prev =
    existing && typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
  kvSet('currentBusiness', { ...prev, id });
  invalidateListKnownBusinessIdsCache();
  return { ok: true, businessId: id };
}

/** Vor Backfill/Migration: echte ID aus Storage, nicht Canvas-Fallback. */
export async function ensureCurrentBusinessPinned() {
  const existing = getCurrentBusinessId({ allowFallback: false });
  if (existing) return { ok: true, businessId: existing, pinned: false };

  const bid = await resolveBestKnownBusinessId();
  if (!bid) {
    return { ok: false, error: 'no_business_in_storage', hint: 'Login or import data first' };
  }
  pinCurrentBusiness(bid);
  return { ok: true, businessId: bid, pinned: true };
}
